import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { vdjColor, vdjBeatChroma, vdjCameraKick, VdjPost, VdjComfort, VDJ_DROP_FLASH } from './lib/vdjLook.js';

// Contrat du rig partagé : interdit. Le néon est sa propre lumière.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'branches',
  family: 'geometric',
  danceability: 0.78,
  requiresTier: 0,
  energyLevel: 0.72,
  bpmMin: 100,
  bpmMax: 150,
  movement: 0.70,
  tonality: 0.65,
  enabled: true
};

// BRANCHES : un arbre fractal de néon qui pousse du bas de l'écran - 9
// niveaux, 2 branches par nœud, 511 segments - jusqu'à une couronne de 256
// rameaux qui fleurissent sur les temps. Il se balance, se métamorphose sur
// les phrases, s'effondre en pluie de pétales sur les drops et repousse.
// House mélodique, trance, techno mélodique, pop électronique. LOOK VDJ party.
//
// L'ARBRE EST ANALYTIQUE (critère 6). Un segment n'a qu'un attribut : son
// indice dans le tas (racine 1, enfants 2n et 2n+1 ; niveau = floor(log2 n)).
// Le vertex shader remonte son chemin depuis le tronc : à chaque niveau, le
// bit du chemin choisit le côté, l'angle tourne de ± l'angle de ramification
// (asymétrie), plus la torsion (spirale), la retombée (saule), un aléa par
// nœud (éclair), le redressement (candélabre), le balancement et la secousse
// du niveau ; la longueur est multipliée par le rapport. Tout cela vient de
// deux vec4 de paramètres et de deux tableaux par niveau (balancement,
// croissance) : le CPU interpole des paramètres, jamais des positions. Une
// métamorphose est donc continue par construction (critère 2).
//
// LE CADRAGE (R6, R19, critère 5) : la même marche, en JS, sur les 511
// nœuds, donne la boîte de l'arbre pour des paramètres donnés ; on en tire
// l'échelle (85 % de la hauteur, moins si la largeur ou une zone
// d'exclusion l'exige) et le décalage qui centre l'arbre horizontalement sur
// le CENTRE DE COMPOSITION. Calculé une fois par transition (9 échantillons
// de la métamorphose, interpolés), pas par frame.
//
// LES TRAITS (R1) : un quad par segment, distance exacte au segment en
// pixels ; 8 px au tronc, 1 px aux rameaux ; cœur blanc chaud, gaine de la
// couleur du niveau, halo exp(-d/τ) éteint avant le bord du quad (R12). Un
// trait de 1 px garde une couverture d'un pixel (boîte de 1 px intégrée) :
// aucun scintillement. Mélange MAX : deux segments qui se rejoignent ne
// s'additionnent pas (pas de perles aux jointures).

const TAU = Math.PI * 2;
const DEG = Math.PI / 180;
const LEVELS_MAX = 9;
const PRESETS = {
  // angle, ratio, asymmetry, twist/level, droop/level, jitter, upright (even levels), trunk
  cypress:    [15, 0.78, 0.00, 0, 0, 0, 0.0, 0.30],
  oak:        [42, 0.70, 0.05, 0, 0, 4, 0.0, 0.26],
  spiral:     [30, 0.74, 0.00, 22, 0, 0, 0.0, 0.26],
  fern:       [36, 0.75, 0.55, 5, 0, 0, 0.0, 0.26],
  coral:      [50, 0.66, 0.10, 0, -4, 12, 0.0, 0.26],
  lightning:  [34, 0.71, 0.00, 0, 0, 38, 0.0, 0.26],
  candelabra: [86, 0.70, 0.00, 0, 0, 0, 1.0, 0.24],
  willow:     [34, 0.76, 0.00, 0, 13, 0, 0.0, 0.28]
};
const PRESET_NAMES = Object.keys(PRESETS);
const BUILD_SPIRAL = [26, 0.74, 0.00, 40, 0, 0, 0.0, 0.26];   // the rise: a tighter and tighter spiral
const TIER = { 0: { levels: 8 }, 1: { levels: 9 }, 2: { levels: 9 } };
const HEIGHT_FRAC = 0.85;
const MORPH_BEATS = 2, COLLAPSE_BEATS = 2, BUILD_BEATS = 32;
const SWAY_DEG = [0.6, 1.0, 1.5, 2.1, 2.8, 3.4, 4.0, 4.6, 5.2];   // per level: grows toward the twigs
const SWAY_LAG = 0.07;                                            // bars of delay per level: the wind climbs
const SHAKE_DEG = 4.5, SHAKE_CLIMB_BEATS = 0.5, SHAKE_LEN_BEATS = 0.18;
const PETALS = 5;
const NO_BPM = 124, SNAP_BEATS = 0.35;
const DROP_SCORE_MIN = 0.7, DROP_STALE_MS = 1500;
const EXCLUSIONS = [['tr', 0.22, 0.12], ['tl', 0.08, 0.12], ['br', 0.22, 0.10]];

function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
// a preset as the shader's two vec4 (radians, units)
export function presetParams(p) {
  return [p[0] * DEG, p[1], p[2], p[3] * DEG, p[4] * DEG, p[5] * DEG, p[6], p[7]];
}
export function mixParams(a, b, t) { return a.map((v, i) => v + (b[i] - v) * t); }
// the per-node jitter: exact in float32 for n <= 511 (the shader computes the same)
function jitterHash(m) { const x = m * 0.7548776662; return x - Math.floor(x); }
const GLSL_HASH = 'float jitterHash(float m) { return fract(m * 0.7548776662); }';

// --- the walk, in JS: the ends of every node for given parameters ------------------
// Returns { a: [x0, y0, ...] starts, b: ends } in tree units (root at the origin, up = +y).
export function walkTree(P, levels, sway) {
  const n = (1 << levels) - 1, A = new Float32Array((n + 1) * 2), B = new Float32Array((n + 1) * 2);
  const ang = new Float32Array(n + 1), len = new Float32Array(n + 1);
  for (let node = 1; node <= n; node++) {
    const L = Math.floor(Math.log2(node));
    let a, l, x0, y0;
    if (L === 0) { a = Math.PI / 2 + (sway ? sway[0] : 0); l = P[7]; x0 = 0; y0 = 0; }
    else {
      const par = node >> 1, s = (node & 1) ? 1 : -1;
      x0 = B[par * 2]; y0 = B[par * 2 + 1];
      a = stepAngle(ang[par], s, L, node, P) + (sway ? sway[L] : 0);
      a = uprightAngle(a, L, P);
      l = len[par] * P[1] * (1 + s * P[2] * 0.25);
    }
    ang[node] = a; len[node] = l;
    A[node * 2] = x0; A[node * 2 + 1] = y0;
    B[node * 2] = x0 + Math.cos(a) * l; B[node * 2 + 1] = y0 + Math.sin(a) * l;
  }
  return { A, B, n };
}
function stepAngle(parentAng, s, L, node, P) {
  let a = parentAng + s * P[0] * (1 + s * P[2]) + P[3] + P[5] * (jitterHash(node) - 0.5) * 2;
  a += P[4] * L * Math.tanh((a - Math.PI / 2) * 4);                 // droop away from the vertical
  return a;
}
function uprightAngle(a, L, P) { return (L % 2 === 0) ? a + (Math.PI / 2 - a) * P[6] : a; }

// the framing: scale and x offset so the tree fills HEIGHT_FRAC of the height,
// sits in the margins and out of the exclusion zones; root at the bottom
export function fitTree(P, levels, aspect) {
  const t = walkTree(P, levels, null);
  let x0 = 0, x1 = 0, y0 = 0, y1 = 0;
  for (let i = 1; i <= t.n; i++) {
    x0 = Math.min(x0, t.B[i * 2]); x1 = Math.max(x1, t.B[i * 2]); y0 = Math.min(y0, t.B[i * 2 + 1]); y1 = Math.max(y1, t.B[i * 2 + 1]);
  }
  const pad = 0.03;                                   // flowers, sway, the shake
  const H = (y1 - y0) * (1 + pad), W = (x1 - x0) * (1 + pad);
  let s = (2 * HEIGHT_FRAC) / H;
  s = Math.min(s, (2 * (aspect - 0.1)) / W);
  // centred horizontally on the stage, its bottom at the bottom of the 85 % band
  const place = (sc) => ({ s: sc, ox: -0.5 * (x0 + x1) * sc, oy: -HEIGHT_FRAC - y0 * sc, box: [x0, x1, y0, y1] });
  const inZones = (pl) => {
    for (let i = 1; i <= t.n; i++) {
      const x = t.B[i * 2] * pl.s + pl.ox, y = t.B[i * 2 + 1] * pl.s + pl.oy;
      if (Math.abs(y) > 0.9 || Math.abs(x) > aspect - 0.1) return true;
      for (const e of EXCLUSIONS) {
        const ew = e[1] * 2 * aspect, eh = e[2] * 2;
        const ex0 = e[0][1] === 'r' ? aspect - ew : -aspect, ex1 = e[0][1] === 'r' ? aspect : -aspect + ew;
        const ey0 = e[0][0] === 't' ? 1 - eh : -1, ey1 = e[0][0] === 't' ? 1 : -1 + eh;
        if (x > ex0 - 0.02 && x < ex1 + 0.02 && y > ey0 - 0.02 && y < ey1 + 0.02) return true;
      }
    }
    return false;
  };
  let pl = place(s);
  for (let k = 0; k < 40 && inZones(pl); k++) { s *= 0.97; pl = place(s); }
  return pl;
}

// --- GLSL -----------------------------------------------------------------------
// The walk: node n (heap index), level L; parameters P0 (angle, ratio, asym,
// twist), P1 (droop, jitter, upright, trunk); sway per level; grow per level
// (the segment's end along its own direction). Out: start, end, angle.
const WALK_GLSL = `
  const float PI = 3.14159265;
  uniform float uSway[${LEVELS_MAX}];
  uniform float uGrow[${LEVELS_MAX}];
  ${GLSL_HASH}
  float tanh4(float x) { float e = exp(-2.0 * clamp(x * 4.0, -10.0, 10.0)); return (1.0 - e) / (1.0 + e); }
  void walk(float n, float L, vec4 P0, vec4 P1, bool useSway, out vec2 a, out vec2 b, out float angOut, out float lenOut) {
    vec2 p = vec2(0.0);
    float ang = PI * 0.5, len = P1.w;
    a = p; b = p;
    for (int k = 0; k < ${LEVELS_MAX}; k++) {
      float fk = float(k);
      if (fk > L + 0.5) break;
      if (k > 0) {
        float m = floor(n / pow(2.0, L - fk) + 1e-3);      // the ancestor at level k
        float s = mod(m, 2.0) > 0.5 ? 1.0 : -1.0;
        ang += s * P0.x * (1.0 + s * P0.z) + P0.w + P1.y * (jitterHash(m) - 0.5) * 2.0;
        ang += P1.x * fk * tanh4(ang - PI * 0.5);
        if (mod(fk, 2.0) < 0.5) ang += (PI * 0.5 - ang) * P1.z;
        len *= P0.y * (1.0 + s * P0.z * 0.25);
      }
      if (useSway) ang += uSway[k];
      if (fk > L - 0.5) {
        a = p;
        b = p + vec2(cos(ang), sin(ang)) * len * (useSway ? uGrow[k] : 1.0);
        angOut = ang; lenOut = len;
        break;
      }
      p += vec2(cos(ang), sin(ang)) * len;
    }
  }
`;
const SAFE_GLSL = `
  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  float safeAtt(vec2 s) {
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(0.45, 1.0, smoothstep(0.0, uSafeFeather, d));
  }
`;
// the frame: tree units -> screen units (height units, the stage at the origin),
// then the bar's camera kick about the stage centre
const FRAME_GLSL = `
  uniform vec3 uPlace;        // scale, x offset, y offset
  uniform vec2 uStage;        // the centre of composition
  uniform vec2 uKick;         // zoom, roll
  vec2 toScreen(vec2 t) {
    vec2 s = t * uPlace.x + uPlace.yz;
    float c = cos(uKick.y), sn = sin(uKick.y);
    s = vec2(c * s.x - sn * s.y, sn * s.x + c * s.y) * uKick.x;
    return s + uStage;
  }
  vec4 toClip(vec2 s) { return vec4(s.x / uAspect, s.y, 0.0, 1.0); }
`;

const segVertexShader = `
  precision highp float;
  ${WALK_GLSL}
  ${SAFE_GLSL}
  ${FRAME_GLSL}
  attribute float iNode;
  attribute float iLevel;
  uniform vec4 uP0;
  uniform vec4 uP1;
  uniform float uPxPerUnit;
  uniform float uPxScale;
  uniform float uLevels;
  varying vec2 vLoc;          // px, along / across the segment, from its start
  varying float vLen;         // px
  varying float vHalf;        // half width, px
  varying float vTau;         // halo, px
  varying float vLevel;
  varying vec2 vScreen;
  void main() {
    vec2 a, b; float ang, len;
    walk(iNode, iLevel, uP0, uP1, true, a, b, ang, len);
    vec2 A = toScreen(a) * uPxPerUnit, B = toScreen(b) * uPxPerUnit;
    vec2 d = B - A; float L = length(d);
    float g = uGrow[int(iLevel + 0.5)];
    if (g < 0.002) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
    vec2 t = L > 1e-4 ? d / L : vec2(0.0, 1.0), nrm = vec2(-t.y, t.x);
    // width: 8 px at the trunk, 1 px at the twigs, geometric in between
    float k = iLevel / max(uLevels - 1.0, 1.0);
    float w = max(1.0, 8.0 * pow(1.0 / 8.0, k) * uPxScale);
    vHalf = 0.5 * w;
    vTau = 1.2 + 0.55 * w;
    float m = vHalf + 6.0 * vTau;                   // R12: the halo dies 6 tau before the quad's edge
    // position.xy in [-1, 1]: x along (with caps), y across
    float along = position.x < 0.0 ? -m : L + m;
    float across = position.y * m;
    vec2 P = A + t * along + nrm * across;
    vLoc = vec2(along, across); vLen = L;
    vLevel = k;
    vScreen = P / uPxPerUnit;
    gl_Position = toClip(vScreen);
  }
`;
const segFragmentShader = `
  precision highp float;
  ${SAFE_GLSL}
  uniform vec3 uColA;
  uniform vec3 uColB;
  uniform float uGain;
  varying vec2 vLoc;
  varying float vLen;
  varying float vHalf;
  varying float vTau;
  varying float vLevel;
  varying vec2 vScreen;
  void main() {
    float h = clamp(vLoc.x, 0.0, vLen);
    float d = length(vec2(vLoc.x - h, vLoc.y));
    // coverage: a box of the stroke's width integrated over one pixel (1 px stays 1 px)
    float cov = clamp(vHalf + 0.5 - d, 0.0, 1.0);
    // the white core (the only thing above the bloom's threshold) only on strokes of 3 px
    // and more, never thinner than a pixel: a sub-pixel core went in and out of the
    // half-resolution bloom's texels and the whole glow breathed by +-5 % as the tree swayed
    // soft-edged (3 px ramp), on strokes of 4 px and more: a hard 1-3 px core went in
    // and out of the half-resolution bloom's texels and the glow breathed by 4 %
    float coreR = max(vHalf - 2.0, 1.0);
    float core = (1.0 - smoothstep(coreR - 1.5, coreR + 1.5, d)) * smoothstep(3.5, 4.5, vHalf * 2.0);
    vec3 col = mix(uColA, uColB, vLevel);
    vec3 c = mix(col * 0.75, vec3(1.0, 0.95, 0.88), core) * cov;
    float halo = 0.35 * exp(-max(d - vHalf, 0.0) / vTau) * (1.0 - smoothstep(4.5 * vTau, 6.0 * vTau, max(d - vHalf, 0.0)));
    c = max(c, col * halo);
    gl_FragColor = vec4(c * uGain * safeAtt(vScreen), 1.0);
  }
`;
// the flowers: a five-pointed star of light at each twig's end, opening in waves
const flowerVertexShader = `
  precision highp float;
  ${WALK_GLSL}
  ${SAFE_GLSL}
  ${FRAME_GLSL}
  attribute float iNode;
  uniform vec4 uP0;
  uniform vec4 uP1;
  uniform float uPxPerUnit;
  uniform float uPxScale;
  uniform float uLeaf;           // the twigs' level
  uniform vec4 uBloom;           // beat phase, wave mode, rest openness, gain (closing in the rise)
  uniform vec4 uBox;             // the tree's screen box: x0, x1, y0, y1 (for the wave's order)
  varying vec2 vQ;
  varying float vOpen;
  varying float vRot;
  varying vec2 vScreen;
  void main() {
    vec2 a, b; float ang, len;
    walk(iNode, uLeaf, uP0, uP1, true, a, b, ang, len);
    float g = uGrow[int(uLeaf + 0.5)];
    vec2 c = toScreen(b);
    // the wave: left to right, right to left, or bottom to top
    float kx = clamp((c.x - uBox.x) / max(uBox.y - uBox.x, 1e-3), 0.0, 1.0);
    float ky = clamp((c.y - uBox.z) / max(uBox.w - uBox.z, 1e-3), 0.0, 1.0);
    float key = uBloom.y < 0.5 ? kx : (uBloom.y < 1.5 ? 1.0 - kx : ky);
    float age = uBloom.x - key * 0.5;
    float open = uBloom.z + (1.0 - uBloom.z) * (age >= 0.0 ? exp(-age / 0.28) : exp(-(age + 1.0) / 0.28));
    open *= uBloom.w * smoothstep(0.6, 1.0, g);
    // the flowers follow the tree's scale: a small tree (a portrait screen) with
    // full-size flowers packed them into a bloomed veil
    float fs = clamp(uPlace.x / 2.0, 0.45, 1.0);
    float size = (5.0 + 7.0 * open) * uPxScale * fs;
    open *= fs;
    vOpen = open;
    vRot = ang - PI * 0.5;
    vQ = position.xy * 1.6;          // the star in [-1, 1], its glow to 1.6
    vScreen = c;
    vec2 P = c * uPxPerUnit + position.xy * size * 1.6;
    gl_Position = toClip(P / uPxPerUnit);
    if (open < 0.01) gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
  }
`;
const flowerFragmentShader = `
  precision highp float;      // the safe-zone uniforms are shared with the vertex stage: same precision
  ${SAFE_GLSL}
  uniform vec3 uColB;
  uniform float uGain;
  varying vec2 vQ;
  varying float vOpen;
  varying float vRot;
  varying vec2 vScreen;
  void main() {
    float r = length(vQ);
    float a = atan(vQ.y, vQ.x) - vRot;
    // a star: radius 0.35 between the points, 1.0 at the points
    float star = 0.35 + 0.65 * pow(abs(cos(2.5 * a)), 6.0);
    float inside = 1.0 - smoothstep(star * 0.85, star, r);
    float core = 1.0 - smoothstep(0.0, 0.30, r);
    // a small glow only: 256 open flowers with a wide one bloomed into a green veil
    float glow = 0.08 * exp(-r * 4.0) * (1.0 - smoothstep(1.0, 1.6, r));
    vec3 c = mix(uColB * 0.75, vec3(1.0, 0.96, 0.9), core) * inside * vOpen + uColB * glow * vOpen;
    gl_FragColor = vec4(c * uGain * safeAtt(vScreen), 1.0);
  }
`;
// the petals: five per flower, ballistic from where the flowers were at the
// drop (the tree's parameters frozen then), tumbling (an elongated sprite)
const petalVertexShader = `
  precision highp float;
  ${WALK_GLSL}
  ${SAFE_GLSL}
  ${FRAME_GLSL}
  attribute vec3 position;       // flower node, petal index, random (position is the attribute)
  uniform vec4 uDP0;
  uniform vec4 uDP1;
  uniform vec3 uDPlace;
  uniform float uLeaf;
  uniform float uT;              // seconds since the drop (< 0: none)
  uniform float uPxPerUnit;
  uniform float uPxScale;
  varying float vAlpha;
  varying float vSpin;
  varying vec2 vScreen;
  void main() {
    if (uT < 0.0) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
    vec2 a, b; float ang, len;
    walk(position.x, uLeaf, uDP0, uDP1, false, a, b, ang, len);
    vec2 f = b * uDPlace.x + uDPlace.yz;
    float pa = ang + (position.y / ${PETALS}.0) * 6.2831853 + position.z * 0.8;
    vec2 v0 = vec2(cos(pa), sin(pa)) * (0.30 + 0.35 * position.z) + vec2(0.0, 0.35);
    vec2 p = f + v0 * uT + vec2(0.0, -0.55) * uT * uT;
    // tumbling: a sideways sway as it falls
    p.x += 0.05 * sin(uT * (5.0 + 3.0 * position.z) + position.z * 6.0) * min(uT, 1.0);
    vScreen = p + uStage;
    vSpin = uT * (7.0 + 5.0 * position.z) + position.z * 6.28;
    vAlpha = (1.0 - smoothstep(1.8, 3.2, uT)) * smoothstep(0.0, 0.05, uT);
    gl_PointSize = (3.0 + 2.0 * position.z) * uPxScale;
    gl_Position = toClip(vScreen);
  }
`;
const petalFragmentShader = `
  precision highp float;      // a raw shader: the uniforms shared with the vertex stage need the same precision
  ${SAFE_GLSL}
  uniform vec3 uColA;
  uniform vec3 uColB;
  varying float vAlpha;
  varying float vSpin;
  varying vec2 vScreen;
  void main() {
    vec2 q = gl_PointCoord - 0.5;
    float c = cos(vSpin), s = sin(vSpin);
    q = vec2(c * q.x - s * q.y, s * q.x + c * q.y);
    // an ellipse whose width breathes with the spin: a petal turning over
    float w = 0.12 + 0.30 * abs(sin(vSpin * 0.7));
    float e = length(q / vec2(w, 0.5));
    float a = (1.0 - smoothstep(0.7, 1.0, e)) * vAlpha;
    vec3 col = mix(uColB, vec3(1.0, 0.95, 0.9), 0.35);
    gl_FragColor = vec4(col * a * safeAtt(vScreen), 1.0);
  }
`;
const flashVertexShader = `void main() { gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }`;
const flashFragmentShader = `
  precision mediump float;
  uniform float uFlash;
  void main() { gl_FragColor = vec4(vec3(${VDJ_DROP_FLASH.toFixed(2)}) * uFlash, 1.0); }
`;

export class BranchesVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null && TIER[o.qualityTier] ? o.qualityTier : 1;
    this._levels = TIER[this._tier].levels;
    const fp = urlParam('branchespreset');
    this._forcePreset = fp && PRESETS[fp] ? fp : null;
    const fg = parseFloat(urlParam('branchesgrow'));
    this._forceGrow = Number.isFinite(fg) ? Math.max(0, Math.min(1, fg)) : null;
    this._forceBloom = urlParam('branchesbloom') === '1';
    this._layers = (urlParam('brancheslayers') || 'segs,flowers,petals').split(',');
    this._forceDrop = urlParam('branchesdrop') === '1';
    this._forceBuild = urlParam('branchesbuild') === '1';
    this._post = new VdjPost({ bloomStrength: 0.35, bloomThreshold: 0.78, grain: 0, vignette: 0.35, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 });
    this._comfort = new VdjComfort();
    if (urlParam('branchesprobe') === '1' && typeof window !== 'undefined') window.__branches = this;
  }

  get postProcessing() { return this._post.value; }

  init(scene) {
    this._scene = scene;
    const levels = this._levels, nSeg = (1 << levels) - 1, leaf = levels - 1, nLeaf = 1 << leaf;
    this._sway = new Array(LEVELS_MAX).fill(0);
    this._grow = new Array(LEVELS_MAX).fill(0);
    const safe = {
      uAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter,
      uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
    };
    const U = this._U = Object.assign({
      uSway: { value: this._sway }, uGrow: { value: this._grow },
      uP0: { value: new THREE.Vector4() }, uP1: { value: new THREE.Vector4() },
      uPlace: { value: new THREE.Vector3(1, 0, -0.85) }, uStage: { value: new THREE.Vector2() }, uKick: { value: new THREE.Vector2(1, 0) },
      uPxPerUnit: { value: 540 }, uPxScale: { value: 1 }, uLevels: { value: levels }, uLeaf: { value: leaf },
      uColA: { value: new THREE.Color(1, 0.2, 0.6) }, uColB: { value: new THREE.Color(0.2, 0.8, 1) }, uGain: { value: 1 },
      uBloom: { value: new THREE.Vector4(0, 0, 0.35, 1) }, uBox: { value: new THREE.Vector4(-1, 1, -1, 1) },
      uDP0: { value: new THREE.Vector4() }, uDP1: { value: new THREE.Vector4() }, uDPlace: { value: new THREE.Vector3(1, 0, 0) }, uT: { value: -1 }
    }, safe);
    const maxBlend = { blending: THREE.CustomBlending, blendEquation: THREE.MaxEquation, blendSrc: THREE.OneFactor, blendDst: THREE.OneFactor };

    // the segments: one instanced quad each, attributes = heap index and level (static)
    const quad = new THREE.PlaneGeometry(2, 2);
    const sg = new THREE.InstancedBufferGeometry();
    sg.setAttribute('position', quad.getAttribute('position')); sg.setIndex(quad.getIndex());
    const iN = new Float32Array(nSeg), iL = new Float32Array(nSeg);
    for (let i = 0; i < nSeg; i++) { iN[i] = i + 1; iL[i] = Math.floor(Math.log2(i + 1)); }
    sg.setAttribute('iNode', new THREE.InstancedBufferAttribute(iN, 1));
    sg.setAttribute('iLevel', new THREE.InstancedBufferAttribute(iL, 1));
    sg.instanceCount = nSeg;
    this._segs = new THREE.Mesh(sg, new THREE.ShaderMaterial(Object.assign({ uniforms: U, vertexShader: segVertexShader, fragmentShader: segFragmentShader, depthTest: false, depthWrite: false, transparent: true }, maxBlend)));
    // the flowers
    const fg = new THREE.InstancedBufferGeometry();
    fg.setAttribute('position', quad.getAttribute('position')); fg.setIndex(quad.getIndex());
    const fN = new Float32Array(nLeaf); for (let i = 0; i < nLeaf; i++) fN[i] = nLeaf + i;
    fg.setAttribute('iNode', new THREE.InstancedBufferAttribute(fN, 1));
    fg.instanceCount = nLeaf;
    this._flowers = new THREE.Mesh(fg, new THREE.ShaderMaterial({ uniforms: U, vertexShader: flowerVertexShader, fragmentShader: flowerFragmentShader, depthTest: false, depthWrite: false, transparent: true, blending: THREE.AdditiveBlending }));
    // the petals: (flower node, petal, random), static
    const pp = new Float32Array(nLeaf * PETALS * 3);
    let seed = 7;
    const rnd = () => { seed = (seed * 1664525 + 1013904223) >>> 0; return seed / 4294967296; };
    for (let i = 0; i < nLeaf; i++) for (let k = 0; k < PETALS; k++) pp.set([nLeaf + i, k, rnd()], (i * PETALS + k) * 3);
    const pg = new THREE.BufferGeometry(); pg.setAttribute('position', new THREE.BufferAttribute(pp, 3));
    this._petals = new THREE.Points(pg, new THREE.RawShaderMaterial({
      uniforms: U, vertexShader: 'precision highp float;\nuniform mat4 projectionMatrix;\n' + petalVertexShader.replace('precision highp float;', ''),
      fragmentShader: petalFragmentShader, depthTest: false, depthWrite: false, transparent: true, blending: THREE.AdditiveBlending
    }));
    this._flashU = { uFlash: { value: 0 } };
    this._flash = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._flashU, vertexShader: flashVertexShader, fragmentShader: flashFragmentShader, depthTest: false, depthWrite: false, transparent: true, blending: THREE.AdditiveBlending }));
    let order = -100;
    for (const m of [this._segs, this._flowers, this._petals, this._flash]) { m.frustumCulled = false; m.renderOrder = order++; scene.add(m); }
    this._segs.visible = this._layers.includes('segs'); this._flowers.visible = this._layers.includes('flowers'); this._petals.visible = this._layers.includes('petals');
    const self = this;
    this._segs.onBeforeRender = function (renderer) {
      const rt = renderer.getRenderTarget();
      const h = rt ? rt.height : renderer.getDrawingBufferSize(_size).y;
      self._U.uPxPerUnit.value = h / 2;
      self._U.uPxScale.value = Math.max(0.75, h / 1080);
    };

    // state
    this._clock = 0; this._clockPrev = 0; this._targetOffset = null;
    this._beatCount = 0; this._barCount = 0; this._phaseBeat = null; this._phaseBar = null; this._phasePhrase = null;
    this._bpm = NO_BPM; this._time = 0;
    this._presetIdx = this._forcePreset ? PRESET_NAMES.indexOf(this._forcePreset) : 0;
    this._from = presetParams(PRESETS[PRESET_NAMES[this._presetIdx]]); this._to = this._from; this._morphStart = null;
    this._growStart = null; this._collapse = null;
    this._lastKickBeat = -9; this._kickSign = 1;
    this._dropT = -1; this._flashFrames = 0;
    this._marksRef = undefined; this._drops = []; this._dropIdx = 0; this._lastNowMs = null;
    this._trackSeen = undefined; this._paletteKey = null; this._palShift = 0; this._lastPhraseAt = 0;
    this._fitKey = ''; this._fits = null;
    this._log = [];
  }

  _note(e) { this._log.push(e); if (this._log.length > 40) this._log.shift(); }

  _tickDrops(profile) {
    const tl = profile.analysisTimeline;
    const marks = tl && tl.marks ? tl.marks : null;
    if (marks !== this._marksRef) {
      this._marksRef = marks; this._drops = [];
      if (marks) for (const m of marks) if (m.type === 'drop' && m.score >= DROP_SCORE_MIN) this._drops.push(m.atMs);
      this._dropIdx = 0;
    }
    const now = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (now == null) return false;
    if (this._lastNowMs != null && (now < this._lastNowMs - 1000 || now > this._lastNowMs + 5000)) {
      this._dropIdx = 0;
      while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) this._dropIdx++;
    }
    this._lastNowMs = now;
    let fired = false;
    while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) {
      if (now - this._drops[this._dropIdx] < DROP_STALE_MS) fired = true;
      this._dropIdx++;
    }
    return fired;
  }

  // the framing along a transition: 9 samples, computed once
  _computeFits(from, to, aspect) {
    const fits = [];
    for (let k = 0; k <= 8; k++) fits.push(fitTree(mixParams(from, to, k / 8), this._levels, aspect));
    return fits;
  }
  _fitAt(e) {
    const f = this._fits, x = Math.max(0, Math.min(1, e)) * 8, i = Math.min(7, Math.floor(x)), u = x - i;
    return { s: f[i].s + (f[i + 1].s - f[i].s) * u, ox: f[i].ox + (f[i + 1].ox - f[i].ox) * u, oy: f[i].oy + (f[i + 1].oy - f[i].oy) * u, box: f[Math.round(x)].box };
  }

  _palette(profile, shift) {
    const pal = profile.artworkPalette;
    const src = (pal && (pal.vividColors || pal.dominantColors)) || [];
    const hsl = { h: 0, s: 0, l: 0 };
    const base = src.length ? vdjColor(src[shift % src.length]) : new THREE.Color().setHSL((0.92 + shift * 0.31) % 1, 0.9, 0.55);
    base.getHSL(hsl);
    let c2 = null;
    for (let k = 1; k < src.length; k++) {
      const t = vdjColor(src[(shift + k) % src.length]), h2 = { h: 0, s: 0, l: 0 }; t.getHSL(h2);
      const d = Math.abs(h2.h - hsl.h); if (Math.min(d, 1 - d) >= 0.25) { c2 = t; break; }
    }
    if (!c2) c2 = new THREE.Color().setHSL((hsl.h + 0.38) % 1, 0.9, 0.55);
    this._U.uColA.value.copy(base); this._U.uColB.value.copy(c2);
  }

  update(deltaTime, profile) {
    const dt = Math.min(Math.max(deltaTime, 0), 0.1);
    const U = this._U;
    if (profile.trackId !== this._trackSeen) {
      this._trackSeen = profile.trackId;
      this._growStart = null;                 // the tree grows again from the start of the track
      this._palShift = 0; this._paletteKey = null;
    }
    const pk = (profile.artworkPalette && String((profile.artworkPalette.vividColors || []).slice(0, 3))) + '/' + this._palShift;
    if (pk !== this._paletteKey) { this._paletteKey = pk; this._palette(profile, this._palShift); }
    U.uStage.value.copy(safeZoneUniforms.uStageCenter.value);
    if (profile.playbackState !== 'PLAYING') { this._flashU.uFlash.value = 0; return; }   // frozen, never black
    this._time += dt;

    // the clock, in beats, on the music's bars
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 0;
    const bp = profile.beatPhase, barIn = profile.barPhase, ph = profile.phrasePhase;
    const hasGrid = bp != null && bpm > 0;
    this._bpm = hasGrid ? bpm : NO_BPM;
    const beatS = 60 / this._bpm;
    this._clockPrev = this._clock;
    if (hasGrid) {
      if (this._phaseBeat != null && bp < this._phaseBeat - 0.5) this._beatCount++;
      this._phaseBeat = bp;
      let target;
      if (barIn != null) {
        if (this._phaseBar != null && barIn < this._phaseBar - 0.5) this._barCount++;
        this._phaseBar = barIn;
        target = 4 * (this._barCount + barIn);
      } else target = this._beatCount + bp;
      if (this._targetOffset == null) this._targetOffset = 4 * Math.round((this._clock - target) / 4);
      const err = target + this._targetOffset - this._clock;
      if (Math.abs(err) > SNAP_BEATS) { this._targetOffset = 4 * Math.round((this._clock - target) / 4); this._clock = target + this._targetOffset; }
      else this._clock += Math.max(dt / beatS * 0.5, dt / beatS + err * Math.min(1, dt / 0.5));
    } else { this._clock += dt / beatS; this._targetOffset = null; }
    const c = this._clock, cp = this._clockPrev;
    const edge = (k) => Math.floor(cp / k) !== Math.floor(c / k) && c > cp;
    if (this._growStart == null) this._growStart = Math.ceil(c);

    // the rise: before a known drop, a tighter and tighter spiral, the flowers close
    const nx = profile.director && profile.director.nextEvent;
    let build = 0;
    if (nx && nx.type === 'drop' && nx.score >= DROP_SCORE_MIN && nx.msUntil > 0) build = Math.max(0, 1 - nx.msUntil / 1000 / (BUILD_BEATS * beatS));
    if (this._forceBuild) build = 0.5 + 0.5 * Math.sin(this._time * 0.5);

    // the phrase: metamorphosis to the next preset in 2 beats, a new palette (a cut)
    let phraseEdge = false;
    if (hasGrid && ph != null) { if (this._phasePhrase != null && ph < this._phasePhrase - 0.5) phraseEdge = true; this._phasePhrase = ph; }
    else if (edge(16)) phraseEdge = true;
    if (!phraseEdge && edge(4) && c - this._lastPhraseAt >= 32) phraseEdge = true;    // a phrase grid that never wraps
    if (phraseEdge && !this._collapse && this._morphStart == null) {
      this._lastPhraseAt = c;
      this._beginMorph(this._forcePreset ? this._presetIdx : (this._presetIdx + 1) % PRESET_NAMES.length, c);
      this._palShift++;
      this._note({ what: 'phrase', beat: +c.toFixed(2), to: PRESET_NAMES[this._presetIdx] });
    }

    // the drop: petals, the branches collapse from the twigs in 2 beats, flash; then it regrows
    let drop = hasGrid && this._tickDrops(profile);
    if (this._forceDrop && edge(4) && Math.floor(c / 4) % 8 === 4) drop = true;
    if (drop && !this._collapse) {
      const cur = this._currentParams(c);
      U.uDP0.value.set(cur[0], cur[1], cur[2], cur[3]); U.uDP1.value.set(cur[4], cur[5], cur[6], cur[7]);
      U.uDPlace.value.copy(U.uPlace.value);
      this._dropT = 0;
      this._collapse = { start: c };
      if (this._comfort.requestFlash(this._time)) this._flashFrames = 1;
      this._note({ what: 'drop', beat: +c.toFixed(2) });
    }
    if (this._dropT >= 0) { this._dropT += dt; if (this._dropT > 4) this._dropT = -1; }
    U.uT.value = this._dropT;

    // the growth per level: a level per beat; the collapse: from the twigs, 2 beats
    const L = this._levels;
    if (this._collapse) {
      const t = c - this._collapse.start;
      for (let k = 0; k < L; k++) {
        const s0 = (L - 1 - k) * COLLAPSE_BEATS / L;
        this._grow[k] = 1 - Math.max(0, Math.min(1, (t - s0) / (COLLAPSE_BEATS / L)));
      }
      if (t >= COLLAPSE_BEATS) {
        // nothing left: a new preset, and it grows again on the next beat
        const next = this._forcePreset ? this._presetIdx : (this._presetIdx + 1) % PRESET_NAMES.length;
        this._presetIdx = next;
        this._from = this._to = presetParams(PRESETS[PRESET_NAMES[next]]);
        this._morphStart = null; this._fits = null;
        this._growStart = Math.ceil(c);
        this._collapse = null;
        this._palShift++;
      }
    }
    if (!this._collapse) {
      const g = this._forceGrow != null ? this._forceGrow * L : c - this._growStart;
      for (let k = 0; k < L; k++) this._grow[k] = Math.max(0, Math.min(1, g - k));
    }
    for (let k = L; k < LEVELS_MAX; k++) this._grow[k] = 0;

    // the parameters: the morph, then the rise toward the tight spiral. The
    // morph's progression FIRST: its last frame ends the morph and drops the
    // framing samples, which must then be recomputed before they are read
    const e = this._morphE(c);
    let P = this._currentParams(c);
    const aspect = safeZoneUniforms.uAspect.value || 1.78;
    if (!this._fits || this._fitKey !== aspect.toFixed(3)) {
      this._fitKey = aspect.toFixed(3);
      this._fits = this._computeFits(this._from, this._to, aspect);
      this._buildFits = [fitTree(P, L, aspect), fitTree(presetParams(BUILD_SPIRAL), L, aspect)];
    }
    let place = this._fitAt(e);
    if (build > 0) {
      const sp = presetParams(BUILD_SPIRAL);
      const bt = Math.min(1, build * 1.2);
      P = mixParams(P, sp, bt);
      const fb = this._buildFits[1];
      // the smaller of the two framings, blended (never out of the frame mid-way)
      place = { s: Math.min(place.s, place.s + (fb.s - place.s) * bt), ox: place.ox + (fb.ox - place.ox) * bt, oy: place.oy + (fb.oy - place.oy) * bt, box: bt > 0.5 ? fb.box : place.box };
    }
    U.uP0.value.set(P[0], P[1], P[2], P[3]); U.uP1.value.set(P[4], P[5], P[6], P[7]);
    U.uPlace.value.set(place.s, place.ox, place.oy);
    const bx = place.box;
    U.uBox.value.set(bx[0] * place.s + place.ox, bx[1] * place.s + place.ox, bx[2] * place.s + place.oy, bx[3] * place.s + place.oy);

    // the sway: one way per bar (it turns back on every bar), the wind climbing level by level;
    // the kick: an angle wave that climbs the tree in half a beat
    if (edge(1)) { this._lastKickBeat = Math.floor(c); this._kickSign = -this._kickSign; }
    for (let k = 0; k < LEVELS_MAX; k++) {
      const sway = SWAY_DEG[k] * DEG * Math.cos(Math.PI * (c / 4 - k * SWAY_LAG));
      const tk = c - this._lastKickBeat - k * SHAKE_CLIMB_BEATS / LEVELS_MAX;
      const shake = tk >= 0 && tk < SHAKE_LEN_BEATS ? Math.sin(Math.PI * tk / SHAKE_LEN_BEATS) : 0;
      // the walk ACCUMULATES the levels' angles: beyond the trunk, a third of the level's sway
      this._sway[k] = sway * (k === 0 ? 1 : 0.35) + this._kickSign * SHAKE_DEG * DEG * shake * (0.4 + 0.6 * k / 8);
    }

    // the bloom: a wave on every beat (left->right, right->left, bottom->top), the flowers close in the rise
    const bph = c - Math.floor(c);
    U.uBloom.value.set(bph, Math.floor(c) % 3, this._forceBloom ? 1 : 0.30, 1 - Math.min(1, build * 1.3));

    // the beat's RGB split; the bar's camera kick about the stage centre
    this._post.update(vdjBeatChroma(bph, beatS));
    const kick = vdjCameraKick(barIn != null ? barIn : (c / 4) % 1, Math.floor(c / 4), 4);
    U.uKick.value.set(kick.zoom, kick.roll);
    this._flashU.uFlash.value = this._flashFrames > 0 ? 1 : 0;
    if (this._flashFrames > 0) this._flashFrames--;
  }

  _beginMorph(next, c) {
    this._from = this._currentParams(c);
    this._presetIdx = next;
    this._to = presetParams(PRESETS[PRESET_NAMES[next]]);
    this._morphStart = c;
    this._fits = null;
  }
  _morphE(c) {
    if (this._morphStart == null) return 1;
    const t = (c - this._morphStart) / MORPH_BEATS;
    if (t >= 1) { this._morphStart = null; this._from = this._to; this._fits = null; return 1; }
    return t * t * (3 - 2 * t);
  }
  _currentParams(c) {
    if (this._morphStart == null) return this._to.slice();
    const t = Math.max(0, Math.min(1, (c - this._morphStart) / MORPH_BEATS));
    return mixParams(this._from, this._to, t * t * (3 - 2 * t));
  }

  dispose() {
    for (const m of [this._segs, this._flowers, this._petals, this._flash]) {
      if (!m) continue;
      m.onBeforeRender = null;
      if (this._scene) this._scene.remove(m);
      m.geometry.dispose(); m.material.dispose();
    }
    if (typeof window !== 'undefined' && window.__branches === this) window.__branches = null;
    this._scene = null;
  }
}

const _size = new THREE.Vector2();
